import { debounce } from 'lodash-es'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { HexColorPicker } from 'react-colorful'

import styles from './ColorPicker.module.css'
import { ColorPickerProps } from '../TagsPopout/TagsPopout.types'
import { DEFAULT_COLOR_OPTIONS } from './ColorPicker.constants'

const isValidHex = (value: string): boolean => /^#[0-9A-Fa-f]{6}$/.test(value)

export const ColorPicker: React.FC<ColorPickerProps> = ({
  value = '#FFFFFF',
  onChange,
  dataTestId,
}) => {
  const isInitialRgb = value.toLowerCase() === 'rgb'
  const [colorValue, setColorValue] = useState(isInitialRgb ? '#FF0000' : value)
  const [hexInput, setHexInput] = useState(isInitialRgb ? 'rgb' : value)
  const [rgbMode, setRgbMode] = useState(isInitialRgb)
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange

  const debouncedOnChange = useCallback(
    debounce((newColor: string) => {
      onChange(newColor)
      setColorValue(newColor)
      setHexInput(newColor)
    }, 200),
    [onChange],
  )

  const applyColor = (color: string) => {
    setRgbMode(false)
    onChange(color)
    setColorValue(color)
    setHexInput(color)
  }

  const handleHexInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value

    // Easter egg: typing "rgb" activates RGB cycle mode
    if (raw.toLowerCase() === '#rgb') {
      setRgbMode(true)
      setHexInput('rgb')
      return
    }

    setRgbMode(false)
    // Ensure value always starts with #
    const val = raw.startsWith('#') ? raw : '#' + raw
    setHexInput(val)

    if (isValidHex(val)) {
      onChange(val)
      setColorValue(val)
    }
  }

  const handleHexInputBlur = () => {
    if (rgbMode) return
    // Revert to last valid color if input is invalid
    if (!isValidHex(hexInput)) {
      setHexInput(colorValue)
    }
  }

  // Persist 'rgb' as the form value when RGB mode activates
  useEffect(() => {
    if (rgbMode) {
      onChangeRef.current('rgb')
    }
  }, [rgbMode])

  return (
    <div className={styles.colorPickerContainer} data-testid={dataTestId}>
      <p className={styles.colorLabel}>Preset Colors</p>
      <div className={styles.colorOptionsContainer} data-testid={`${dataTestId}-options`}>
        {DEFAULT_COLOR_OPTIONS.map((color: string, index: number) => (
          <div
            key={index}
            className={
              styles.colorOption +
              (!rgbMode && color === colorValue ? ' ' + styles.selectedColor : '')
            }
            style={{ backgroundColor: color }}
            data-testid={`${dataTestId}-color-option-${index}`}
            onClick={() => applyColor(color)}
          />
        ))}
      </div>
      <div className={styles.colorInfoContainer}>
        <p className={styles.colorLabel}>Custom Color</p>
        <div className={styles.colorPreviewContainer}>
          <input
            className={[styles.hexInput, rgbMode ? styles.rgbInput : ''].join(' ')}
            value={hexInput}
            onChange={handleHexInputChange}
            onBlur={handleHexInputBlur}
            maxLength={7}
            spellCheck={false}
            data-testid={`${dataTestId}-hex-input`}
          />
          <div
            className={[styles.colorPreviewTile, rgbMode ? styles.rgbTile : ''].join(' ')}
            style={rgbMode ? undefined : { backgroundColor: colorValue }}
          />
        </div>
      </div>

      <div className={styles.colorPickerComponentContainer} data-testid={`${dataTestId}-picker`}>
        <HexColorPicker
          className={styles.colorPickerComponent}
          color={rgbMode ? '#FF0000' : value}
          onChange={debouncedOnChange}
        />
      </div>
    </div>
  )
}
